import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../../services/api';
import NoteGenerationPanel from '../../components/admin/NoteGenerationPanel';
import NoteViewer from '../../components/admin/NoteViewer';
import { useMessages } from '../../hooks/useAutoDismissMessage';

const CaseNotesPage = ({ caseId }) => {
  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [caseInfo, setCaseInfo] = useState(null);
  const [summary, setSummary] = useState(null);

  // Additional state for case details and audio sessions
  const [caseDetails, setCaseDetails] = useState(null);
  const [audioSessions, setAudioSessions] = useState([]);
  const [loadingCase, setLoadingCase] = useState(true);
  const [loadingAudio, setLoadingAudio] = useState(true);
  const [caseError, setCaseError] = useState(null);
  const [audioError, setAudioError] = useState(null);

  // Note generation state
  const [showGeneratePanel, setShowGeneratePanel] = useState(false);

  // Transcription rerun state
  const [rerunSessionId, setRerunSessionId] = useState(null);

  // Auto-dismissing messages (5 seconds)
  const {
    success: rerunSuccess,
    error: rerunError,
    setSuccess: setRerunSuccess,
    setError: setRerunError
  } = useMessages(5000);

  // Format conversion state (per note)
  const [noteFormats, setNoteFormats] = useState({}); // { noteId: { html, plain_text, rtf, json } }
  const [convertingNotes, setConvertingNotes] = useState({}); // { noteId: 'loading' | 'idle' }
  const [convertErrors, setConvertErrors] = useState({}); // { noteId: errorObj }

  useEffect(() => {
    fetchCaseNotes();
    fetchCaseDetails();
    fetchAudioSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  const fetchCaseNotes = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await adminApi.get(`/healthcare/cases/${caseId}/notes/`);

      if (response.data.success) {
        const visitNotes = response.data.visit_notes || [];
        setNotes(visitNotes);
        setCaseInfo(response.data.case_info);
        setSummary(response.data.summary);

        // Populate noteFormats from API response
        const formats = {};
        visitNotes.forEach(note => {
          if (note.formats) {
            formats[note.id] = note.formats;
          }
        });
        setNoteFormats(formats);
      } else {
        setError(response.data.error || 'Failed to load case notes');
      }
    } catch (err) {
      console.error('Error fetching case notes:', err);
      if (err.response && err.response.status === 404) {
        setError('Case not found');
      } else {
        setError(`Error: ${err.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCaseDetails = async () => {
    try {
      setLoadingCase(true);
      const response = await adminApi.getCaseDetails(caseId);
      if (response.data && response.data.success) {
        setCaseDetails(response.data.case);
      } else {
        setCaseError(response.data?.message || 'Failed to fetch case details');
      }
    } catch (err) {
      console.error('Error fetching case details:', err);
      setCaseError(err.message || 'An error occurred while fetching case details');
    } finally {
      setLoadingCase(false);
    }
  };

  const fetchAudioSessions = async () => {
    try {
      setLoadingAudio(true);
      const response = await adminApi.getCaseAudioSessions(caseId);
      if (response.data && response.data.sessions) {
        setAudioSessions(response.data.sessions);
      } else {
        setAudioError('Failed to fetch audio sessions');
      }
    } catch (err) {
      console.error('Error fetching audio sessions:', err);
      setAudioError(err.message || 'An error occurred while fetching audio sessions');
    } finally {
      setLoadingAudio(false);
    }
  };

  const handleDownloadAudio = async (sessionId, downloadType, segmentNumber = null) => {
    setAudioError(null);

    try {
      const downloadData = {
        case_id: caseId,
        session_id: sessionId,
        download_type: downloadType
      };
      if (segmentNumber !== null) {
        downloadData.segment_number = segmentNumber;
      }
      const response = await adminApi.downloadAudioFile(downloadData);
      if (response.data && response.data.success && response.data.download_url) {
        window.open(response.data.download_url, '_blank');
      } else {
        const errorMessage = response.data?.error || 'Failed to generate download link';
        setAudioError(errorMessage);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Error downloading audio file';
      setAudioError(errorMessage);
    }
  };

  const handleNoteGenerated = async (response) => {
    // Refresh notes list
    await fetchCaseNotes();
    // Close generation panel
    setShowGeneratePanel(false);
  };

  const handleRerunTranscription = async (sessionId) => {
    if (rerunSessionId) {
      setRerunError('Another transcription is already being rerun');
      return;
    }
    setRerunError(null);
    setRerunSuccess(null);
    setRerunSessionId(sessionId);
    try {
      await adminApi.triggerTranscription(sessionId);
      setTimeout(() => {
        setRerunSessionId(null);
        setRerunSuccess('Transcription rerun started. Click "Refresh Status" to check progress.');
      }, 1000);
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to rerun transcription';
      setRerunError(errorMessage);
      setRerunSessionId(null);
    }
  };

  const handleRefreshAudioStatus = async () => {
    setAudioError(null);
    try {
      setLoadingAudio(true);
      const response = await adminApi.getCaseAudioSessions(caseId);
      if (response.data?.sessions) {
        setAudioSessions(response.data.sessions);
      } else {
        setAudioError('Failed to refresh audio sessions');
      }
    } catch (err) {
      setAudioError(err.message || 'Error refreshing audio sessions');
    } finally {
      setLoadingAudio(false);
    }
  };

  const handleConvertFormat = async (noteId, formatType) => {
    setConvertingNotes(prev => ({ ...prev, [noteId]: 'loading' }));
    setConvertErrors(prev => ({ ...prev, [noteId]: null }));

    try {
      const response = await adminApi.post('/note/convert/', {
        note_id: noteId,
        content_type: formatType
      });

      if (response.data) {
        setNoteFormats(prev => ({
          ...prev,
          [noteId]: {
            ...prev[noteId],
            [formatType]: response.data[formatType]
          }
        }));
      }
      setConvertingNotes(prev => ({ ...prev, [noteId]: 'idle' }));
    } catch (err) {
      setConvertErrors(prev => ({
        ...prev,
        [noteId]: err.response?.data || { error: 'Failed to convert format' }
      }));
      setConvertingNotes(prev => ({ ...prev, [noteId]: 'error' }));
    }
  };


  if (isLoading) {
    return (
      <div className="d-flex justify-content-center p-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading notes...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger m-3" role="alert">
        <i className="fas fa-exclamation-triangle me-2"></i>
        {error}
      </div>
    );
  }

  return (
    <div className="case-notes-container">
      {/* Case Details */}
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-title text-primary">Case Details</h6>
          {loadingCase ? (
            <p>Loading case details...</p>
          ) : caseError ? (
            <div className="alert alert-danger">{caseError}</div>
          ) : caseDetails ? (
            <div>
              <p><strong>MiraSol Case ID:</strong> {caseId}</p>
              <p><strong>Core Server Case ID:</strong> {caseDetails.CoreServer_CaseID}</p>
              <p><strong>Created on:</strong> {new Date(caseDetails.created_at).toLocaleString([], { timeZoneName: 'short' })}</p>
              <p>
                <strong>Patient:</strong> {caseDetails.patient_info_fk ? (
                  <>
                    <Link to={`/admin/patients/${caseDetails.patient_info_fk.patient_id}`} className="text-primary">
                      {caseDetails.patient_info_fk.first_name} {caseDetails.patient_info_fk.last_name}
                    {' '}(DOB: {new Date(caseDetails.patient_info_fk.dob).toString() !== 'Invalid Date' ? new Date(caseDetails.patient_info_fk.dob).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }) : caseDetails.patient_info_fk.dob} | Core Server ID: {caseDetails.patient_info_fk.patient_id})
                    </Link>
                  </>
                ) : 'N/A'}
              </p>
              <p>
                <strong>Provider:</strong> {caseDetails.provider_info_fk ? (
                  <>
                    <Link to={`/admin/providers/${caseDetails.provider_info_fk.id}`} className="text-primary">
                      {caseDetails.provider_info_fk.first_name} {caseDetails.provider_info_fk.last_name}
                    {' '}(Core Server ID: {caseDetails.provider_info_fk.provider_id})
                    </Link>
                  </>
                ) : 'N/A'}
              </p>
              <p>
                <strong>Health System:</strong> {caseDetails.healthcare_info_fk ? (
                  <Link to={`/admin/health-systems/${caseDetails.healthcare_info_fk.id}`} className="text-primary">
                    {caseDetails.healthcare_info_fk.name}
                  </Link>
                ) : 'N/A'}
              </p>
            </div>
          ) : (
            <p>Case details not found.</p>
          )}
        </div>
      </div>

      {/* Video Conference Details */}
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-title text-primary">Video Conference Details</h6>
          {loadingCase ? (
            <p>Loading Video Conference details...</p>
          ) : caseError ? (
            <div className="alert alert-danger">{caseError}</div>
          ) : caseDetails?.latest_zoom_meeting ? (
            <div>
              <p><strong>Topic:</strong> {caseDetails.latest_zoom_meeting.topic}</p>
              <p><strong>Passcode:</strong> {caseDetails.latest_zoom_meeting.passcode}</p>
              <p><strong>Created at:</strong> {new Date(caseDetails.latest_zoom_meeting.created_at).toLocaleString([], { timeZoneName: 'short' })}</p>
            </div>
          ) : (
            <p>Not found. Visit may have been initiated without request for a new video session.</p>
          )}
        </div>
      </div>

      {/* Audio Sessions & Transcripts */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="card-title text-primary d-flex justify-content-between align-items-center mb-3">
            Audio Sessions & Files
            <button className="btn btn-sm btn-outline-secondary" onClick={handleRefreshAudioStatus} disabled={loadingAudio}>
              <i className="fa fa-refresh me-1"></i>Refresh Status
            </button>
          </div>
          {rerunError && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {rerunError}
              <button type="button" className="btn-close" onClick={() => setRerunError(null)}></button>
            </div>
          )}
          {rerunSuccess && (
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              {rerunSuccess}
              <button type="button" className="btn-close" onClick={() => setRerunSuccess(null)}></button>
            </div>
          )}
          {loadingAudio ? (
            <div className="text-center my-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading audio sessions...</p>
            </div>
          ) : audioError ? (
            <div className="alert alert-danger d-flex justify-content-between align-items-center">
              <span>{audioError}</span>
              <button className="btn btn-sm btn-outline-light border-0" onClick={() => setAudioError(null)}>
                <i className="fa fa-repeat me-1"></i>Retry
              </button>
            </div>
          ) : audioSessions.length === 0 ? (
            <div>No audio sessions found for this case.</div>
          ) : (
            <div>
              {audioSessions.map((session, index) => (
                <div key={session.session_id} className="border rounded p-3 mb-3">
                  <div className="row">
                    <div className="col-md-6">
                      <h6 className="text-primary">Session {index + 1}</h6>
                      <p><strong>Session ID:</strong> {session.session_id}</p>
                      <p><strong>Status:</strong> <span className={`badge ${session.status === 'completed' ? 'bg-success' : session.status === 'failed' ? 'bg-danger' : 'bg-warning'}`}>{session.status}</span></p>
                      <p><strong>Created:</strong> {new Date(session.created_at).toLocaleString()}</p>
                      {session.completed_at && <p><strong>Completed:</strong> {new Date(session.completed_at).toLocaleString()}</p>}
                      <p><strong>Segments:</strong> {session.segment_count}</p>
                      {session.error_message && (
                        <div className="alert alert-danger mt-2 mb-2 py-2" role="alert">
                          <small><strong>Error:</strong> {session.error_message}</small>
                        </div>
                      )}
                      <div className="mt-2">
                        {(() => {
                          const hasAudioFiles = session.audio_files && session.audio_files.some(f => f.file_size_bytes > 0);
                          const isDisabled = !hasAudioFiles || rerunSessionId === session.session_id || session.status === 'processing';

                          return (
                            <>
                              {hasAudioFiles && (<button
                                className={`btn btn-sm ${session.status === 'failed' ? 'btn-warning' : 'btn-outline-primary'}`}
                                onClick={() => handleRerunTranscription(session.session_id)}
                                disabled={isDisabled}
                                title={!hasAudioFiles ? 'No audio files available for transcription' : ''}
                              >
                                {rerunSessionId === session.session_id ? (
                                  <><span className="spinner-border spinner-border-sm me-1" />Starting...</>
                                ) : session.status === 'processing' ? (
                                  <><span className="spinner-border spinner-border-sm me-1" />Processing...</>
                                ) : session.status === 'failed' ? (
                                  <><i className="fa fa-exclamation-triangle me-1"></i>Retry Failed Transcription</>
                                ) : (
                                  <><i className="fa fa-refresh me-1"></i>Rerun Transcription</>
                                )}
                              </button>
                              )}
                              {session.status === 'processing' && hasAudioFiles && (
                                <small className="text-muted d-block mt-1">Click &quot;Refresh Status&quot; above to check progress</small>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <h6 className="text-secondary">Audio Files</h6>
                      {session.segment_count > 1 && (
                        <div className="mb-2">
                          <strong>Full Session:</strong>
                          <br />
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleDownloadAudio(session.session_id, 'concatenated')}
                          >
                            <i className="fa fa-download me-1"></i>
                            Download Complete Session
                          </button>
                        </div>
                      )}
                      {session.audio_files && session.audio_files.length > 0 && (
                        <div>
                          <strong>Individual Segments:</strong>
                          <div className="mt-2">
                            {session.audio_files.map((file, fileIndex) => (
                              <div key={fileIndex} className="mb-2">
                                <div className="d-flex justify-content-between align-items-center">
                                  <span className="text-muted">
                                    Segment {file.segment_number} ({file.source})
                                    {file.duration_seconds && ` - ${Math.round(file.duration_seconds / 60)}:${(file.duration_seconds % 60).toString().padStart(2, '0')}`}
                                  </span>
                                  {file.file_size_bytes > 0 ? (
                                    <button
                                      className="btn btn-sm btn-outline-secondary"
                                      onClick={() => handleDownloadAudio(session.session_id, 'segment', file.segment_number)}
                                    >
                                      <i className="fa fa-download"></i></button>
                                  ) : (
                                    <span className="text-muted">No file available</span>
                                  )}
                                </div>
                                <div className="progress" style={{ height: '4px' }}>
                                  <div
                                    className={`progress-bar ${file.transcription_status === 'completed' ? 'bg-success' : file.transcription_status === 'failed' ? 'bg-danger' : 'bg-warning'}`}
                                    style={{ width: file.transcription_status === 'completed' ? '100%' : file.transcription_status === 'processing' ? '50%' : '25%' }}
                                  ></div>
                                </div>
                                <small className="text-muted">Status: {file.transcription_status}</small>
                                {file.transcription_status === 'failed' && file.error_message && (
                                  <div className="alert alert-danger mt-1 mb-0 py-1 px-2" role="alert">
                                    <small><strong>Error:</strong> {file.error_message}</small>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Visit Notes */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="card-title text-primary d-flex justify-content-between align-items-center mb-3">
            Visit Notes
            <button className="btn btn-sm btn-primary" onClick={() => setShowGeneratePanel(!showGeneratePanel)} >
              <i className="fas fa-plus-circle me-1"></i>
              Generate New Note
            </button>
          </div>

          {/* Note Generation Panel */}
          <NoteGenerationPanel
            caseId={caseId}
            providerId={caseDetails?.provider_info_fk?.id}
            healthSystemId={caseDetails?.healthcare_info_fk?.id}
            audioSessions={audioSessions}
            onNoteGenerated={handleNoteGenerated}
            isOpen={showGeneratePanel}
            onClose={() => setShowGeneratePanel(false)}
          />

          {isLoading ? (
            <div className="text-center my-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading notes...</p>
            </div>
          ) : error ? (
            <div className="alert alert-danger">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </div>
          ) : (
            <>
              {/* Notes Summary */}
              {summary && summary.total_notes > 0 && (
                <div className="mb-3 pb-2">
                  <div className="d-flex justify-content-between text-muted small">
                    <div>
                      <i className="fas fa-file-medical me-2"></i>
                      <strong>{summary.total_notes}</strong> note{summary.total_notes !== 1 ? 's' : ''}
                    </div>
                    <div>
                      <i className="fas fa-clipboard-list me-2"></i>
                      <strong>{summary.templates_used}</strong> template{summary.templates_used !== 1 ? 's' : ''} used
                    </div>
                  </div>
                </div>
              )}

              {/* Notes List */}
              {notes.length === 0 ? (
                <div>No notes found for this case.</div>
              ) : (
                <div className="notes-list">
                  {notes.map((note) => (
                    <NoteViewer
                      key={note.id}
                      note={note}
                      mode="compact"
                      formats={noteFormats[note.id]}
                      onConvertFormat={(formatType) => handleConvertFormat(note.id, formatType)}
                      convertStatus={convertingNotes[note.id] || 'idle'}
                      convertError={convertErrors[note.id]}
                      allowRegenerate={false}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CaseNotesPage;
